import { ReactNode, useState } from 'react'
import { Button, Modal } from '@systemeio/components'
import { LogOut01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import useUser from 'shared/hooks/use-user'
import useIsUserMember from '../hooks/use-is-user-member'
import { CommunityInterface } from '../types/community-interface'

interface CommunityCartProps {
  community: CommunityInterface
  onLeaveCommunity: (community: CommunityInterface) => void
  isFetching: boolean
}

const CommunityCart = ({
  community,
  onLeaveCommunity,
  isFetching,
}: CommunityCartProps): ReactNode => {
  const { t } = useTranslation()
  const { isOwner } = useUser()

  const [opened, setOpened] = useState<boolean>(false)

  const { isUserMember } = useIsUserMember(community.path, () => {}, false)

  return (
    <>
      <div className="group relative w-full max-w-[385px] overflow-hidden rounded-lg bg-bg-primary">
        {!isOwner && isUserMember && (
          <Button
            variant="ghost-gray"
            size="icon-md"
            className="absolute top-3 right-3 hidden group-hover:block"
            onClick={() => setOpened(true)}
          >
            <LogOut01Icon className="size-5" />
          </Button>
        )}
        {community.imagePath ? (
          <img src={community.imagePath} alt={community.name} className="w-full" />
        ) : (
          <img src="/demo_community_image.png" alt={community.name} />
        )}
        <div className="px-5 py-6">
          <Link
            href={`/community/${community.path}`}
            className="mb-3 block text-2xl font-semibold text-text-primary"
            shallow
          >
            {community.name}
          </Link>
        </div>
        <div className="flex justify-end bg-bg-page/30 px-5 py-3">
          <Button
            variant="primary"
            render={<LinkWithoutPrefetch href={`/community/${community.path}`} />}
          >
            {t('community.button_enter_community.title')}
          </Button>
        </div>
      </div>
      <Modal.Root
        open={opened}
        onOpenChange={value => {
          if (!value) setOpened(false)
        }}
      >
        <Modal.Popup>
          <Modal.Header className="pr-16">
            <Modal.Title>{t('community.leave_modal_tittle', { name: community.name })}</Modal.Title>
          </Modal.Header>
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">
            <p className="text-center text-base text-text-primary">
              {t('community.leave_modal_sub_title')}
            </p>
          </div>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setOpened(false)}>
              {t('confirmation_modal.cancel')}
            </Button>
            <Button
              variant="destructive"
              isLoading={isFetching}
              onClick={() => onLeaveCommunity(community)}
            >
              {t('confirmation_modal.leave')} <LogOut01Icon className="size-5" />
            </Button>
          </Modal.Footer>
        </Modal.Popup>
      </Modal.Root>
    </>
  )
}

export default CommunityCart
